import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import layout from '../../../../Modules/layout.module.css';
import { useWorkflowContext } from '../../../../Hooks/useWorkflowContext';
import Button from '../../../../Components/Utils/Button/Button';
import FieldCreationBar from '../../../../Components/Utils/Actions/FieldCreationBar/FieldCreationBar';
import ActionBar from '../../../../Components/Utils/Actions/ActionBar/ActionBar';
import TitledField from '../../../../Components/Utils/TitledField/TitledField';
import ReorderableItem from '../../../../Components/Utils/ReorderableItem/ReorderableItem';
import { AdditionalInfoFieldType, DDCell, DDRow } from '../../../../Types/AdditionalInformation/AdditionalInfoField';
import { axiosInstance } from '../../../../Authentication/AxiosInterceptor';
import client, { getAiPowered, getClient, getClientHasThirdPartySubsets, getClientRolePermission } from '../../../../Redux/Store/Slices/client';
import { useAppSelector } from '../../../../Redux/Store/Store';
import { CompleteQuestionnaireTemplateById, CompleteTemplateWithoutMandate } from '../../../../API/TemplateBank/TemplateBank';
import ConfirmAction from '../../../../Components/Utils/ConfirmAction/ConfirmAction';
import { beginPendingSave } from '../../../../Hooks/useUnsavedChangesWarning';

type AdditionalInformationProps = object;

// Helper function to flatten DDRow[] to DDCell[]
const flattenRows = (rows: DDRow[]): DDCell[] => {
  const allCells: DDCell[] = [];
  rows.forEach((row, rowIndex) => {
    row.cells.forEach(cell => {
      cell.rowNumber = rowIndex;
      allCells.push(cell);
    });
  });
  return allCells;
};

// Helper function to group DDCell[] back into DDRow[]
const groupIntoRows = (cells: DDCell[]): DDRow[] => {
  const rowMap = new Map<number, DDCell[]>();
  
  cells.forEach(cell => {
    const rowNum = cell.rowNumber;
    if (!rowMap.has(rowNum)) {
      rowMap.set(rowNum, []);
    }
    rowMap.get(rowNum)!.push(cell);
  });
  
  const rows: DDRow[] = [];
  rowMap.forEach((cells) => {
    rows.push({ 
      id: crypto.randomUUID(), // Generate a new ID for the row
      cells 
    });
  });
  
  return rows;
};

const AdditionalInformation = (_: AdditionalInformationProps) => {
  const { templateId, templateTitle } = useParams();
  const { getWorkflowPath, isAdminContext } = useWorkflowContext();
  const [fields, setFields] = useState<DDCell[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [confirmNonAIVisible, setConfirmNonAIVisible] = useState(false);

  const client = useAppSelector(getClient);
  const rolePermissions = useAppSelector(getClientRolePermission);
  const hasThirdPartySubsets = useAppSelector(getClientHasThirdPartySubsets);

  const navigate = useNavigate();

  const isAiPowered = useAppSelector(getAiPowered);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const endPendingSaveRef = useRef<(() => void) | null>(null);
  const skipAutosaveRef = useRef(true);

  const saveFields = useCallback(async (cells: DDCell[]) => {
    const rows = groupIntoRows(cells);
    await axiosInstance.post(`UpdateDataBoxesByTemplateId/${templateId}`, rows);
  }, [templateId]);

  useEffect(() => {
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    if (!endPendingSaveRef.current) {
      endPendingSaveRef.current = beginPendingSave();
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveFields(fields);
      } finally {
        endPendingSaveRef.current?.();
        endPendingSaveRef.current = null;
        saveTimerRef.current = null;
      }
    }, 500);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [fields, saveFields]);

  useEffect(() => {
    return () => {
      endPendingSaveRef.current?.();
      endPendingSaveRef.current = null;
    };
  }, []);




  const getDataBoxes = useCallback(async () => {
    const response = await axiosInstance.post<DDRow[]>(`GetDataBoxesByTemplateId/${templateId}`);
    const flattenedCells = flattenRows(response.data);
    skipAutosaveRef.current = true;
    setFields(flattenedCells);
  }, [templateId]);

  useEffect(() => {
    getDataBoxes();
  }, [getDataBoxes]);

  const handleCreateField = async (fieldType: AdditionalInfoFieldType, title: string, options?: string[]) => {
    const newField: DDCell = {
      id: `field-${Date.now()}`,
      type: fieldType,
      header: title,
      tempData: options ? options.map(option => ({ Id: crypto.randomUUID(), Value: option })) : [],
      selection: [],
      text: '',
      date: new Date(0),
      state: false,
      rowNumber: 0,
    };
    setFields([...fields, newField]);
  };

  const handleItemSelected = (fieldId: string, selected: boolean) => {
    setSelectedItems(prevState => {
      if (selected) {
        return [...prevState, fieldId];
      } else {
        return prevState.filter(id => id !== fieldId);
      }
    });
  };

  const handleDeleteItems = async () => {
    setFields(fields.filter(field => !selectedItems.includes(field.id)));
    setSelectedItems([]);
  };

  const handleTitleChange = async (fieldId: string, newTitle: string) => {
    setFields(fields.map(field => 
      field.id === fieldId ? { ...field, header: newTitle } : field
    ));
  };

  const handleValueChange = (fieldId: string, newValue: string) => {
    // For now, just log the value change - in a real implementation, this would be saved
    console.log(`Field ${fieldId} value changed to:`, newValue);
  };

  const handleOptionsChange = async (fieldId: string, newOptions: string[]) => {
    setFields(fields.map(field => 
      field.id === fieldId ? { 
        ...field, 
        tempData: newOptions.map(option => ({ Id: crypto.randomUUID(), Value: option }))
      } : field
    ));
  };

  const handleMoveField = async (fieldId: string, direction: 'up' | 'down') => {
    const currentIndex = fields.findIndex(field => field.id === fieldId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    const newFields = [...fields];
    const [movedField] = newFields.splice(currentIndex, 1);
    newFields.splice(newIndex, 0, movedField);
    
    // Update row numbers after reordering
    newFields.forEach((field, index) => {
      field.rowNumber = index;
    });
    
    setFields(newFields);
  };

  const completeTemplateFinal = async () => {
    if(templateId && templateTitle && client) {

      // Complete template - use different functions based on workflow context
      if (isAdminContext) {
        await CompleteQuestionnaireTemplateById(templateId, client.id);
      } else {
        await CompleteTemplateWithoutMandate(templateId, client.id);
      }
      
      // Navigate based on admin context and permissions
      if (isAdminContext) {
        // Check if user can mandate to 3rd party orgs or departments AND has 3rd party subsets
        const canMandatePartners = rolePermissions?.mandatePartnerTemplates;
        const canMandateDepartments = rolePermissions?.mandateDepartmentTemplates;
        
        if ((canMandatePartners || canMandateDepartments) && hasThirdPartySubsets) {
          navigate('/admin/mandate-templates');
        } else {
          navigate('/admin/manage-templates/active');
        }
      } else {
        // For portal context, navigate to mandate template page
        navigate(getWorkflowPath(templateId, templateTitle, 'mandate-template'));
      }
    }
  }

  const completeTemplate = async () => {

    const response = await axiosInstance.get(`CheckCriteriaHaveQuestions/${templateId}`);

    if(response.data) {
      setConfirmNonAIVisible(true);
      return;
    }

    // Complete the template
    await completeTemplateFinal();
  }

  return (
    <div className={layout.container}>
      <div className={layout.column1}>
        <div className={`${layout.column1}`}>
          <h4>{templateTitle}</h4>
          <p className={layout.fontMedium}>Additional Information</p>
        </div>

        <div className={layout.column1}>

          {fields.length > 0 && (
            <div className={layout.column1}>
              <ActionBar 
                enabledFeatures={['delete']} 
                selectedCount={selectedItems.length} 
                onDeleteHandler={handleDeleteItems}
              />
              
              <p className={layout.fontMedium}>Fields ({fields.length})</p>
              <AnimatePresence mode="popLayout">
                {fields.map((field, index) => (
                  <ReorderableItem
                    key={field.id}
                    id={field.id}
                    index={index}
                    totalItems={fields.length}
                    onMoveUp={() => handleMoveField(field.id, 'up')}
                    onMoveDown={() => handleMoveField(field.id, 'down')}
                  >
                    <TitledField
                      title={field.header}
                      text=""
                      titleChanged={(newTitle) => handleTitleChange(field.id, newTitle)}
                      onSelectedHandler={(selected) => handleItemSelected(field.id, selected)}
                      selected={selectedItems.includes(field.id)}
                      additionalFieldType={field.type}
                      additionalFieldOptions={field.tempData.map(item => item.Value)}
                      onAdditionalValueChange={(value) => handleValueChange(field.id, value)}
                      onOptionsChange={(options) => handleOptionsChange(field.id, options)}
                    />
                  </ReorderableItem>
                ))}
              </AnimatePresence>
            </div>
          )}

          <FieldCreationBar
            onCreateHandler={handleCreateField}
            placeholder="Enter field title..."
            noTextMessage="Please enter a field title"
          />
        </div>

        <div className={`${layout.column1} ${layout.container}`}>
          <div className={layout.rowReverse}><p>Next: Complete Template</p></div>
          <div className={`${layout.rowSpan}`}>
            {!isAiPowered ? ( 
              <Button clickHandler={() => navigate(getWorkflowPath(templateId || '', templateTitle || '', 'define-structure'))}>Back: Define Structure</Button>
            ) : (
              <Button clickHandler={() => navigate(getWorkflowPath(templateId || '', templateTitle || '', 'define-questions'))}>Back: Define Questions</Button>
            )}
              <Button clickHandler={completeTemplate}>Complete Template</Button>
          </div>
        </div>

        <ConfirmAction
          isVisible={confirmNonAIVisible}
          onClose={() => setConfirmNonAIVisible(false)}
          clickHandler={completeTemplateFinal}
          title="Criteria without diagnostic questions"
          message="Some criteria do not have diagnostic questions attached. Some AI powered features will be disabled for these criteria. Are you sure you want to complete the template?"
          confirmText="Complete Template"
          cancelText="Cancel"
          icon="/img/sparkle.svg"
          severity="warning"
          loading={false}
          />
        </div>
    </div>
  );
};

export default AdditionalInformation;

